import React, { useState } from 'react';
import { useLateFeePayments } from '../hooks';
import { Card, CardContent } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/FormField';
import { Button } from '../../../components/ui/Button';
import { Money } from '../../../components/ui/Money';
import { CustomerPicker } from '../../customers/components/CustomerPicker';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../lib/locale';
import { Skeleton } from '../../../components/ui/Skeleton';

interface LateFeePayment {
  id: string;
  receiptNumber: string;
  receiptDate: string;
  customerName: string;
  customerNumber: string;
  agentNumber: string;
  amountPaise: number;
  status: string;
  month: string;
  accrualDate: string;
  feeAmountPaise: number;
  subscriberId: string;
}

interface LateFeePaymentsMeta {
  page: number;
  total: number;
}

export function LateFeePaymentsList() {
  const [date, setDate] = useState<string>('');
  const [customerId, setCustomerId] = useState<string>('');
  const [page, setPage] = useState(1);

  const { data, isLoading, isError, refetch } = useLateFeePayments({ 
    date: date || undefined, 
    customerId: customerId || undefined, 
    page, 
    limit: 20 
  });
  const r = data as { data: LateFeePayment[]; meta: LateFeePaymentsMeta } | undefined;

  const payments = r?.data || [];
  const meta = r?.meta || { page: 0, total: 0 };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <label className="block text-sm font-medium mb-1">Payment Date</label>
          <Input
            type="date"
            className="w-full border border-surface-3 rounded p-2"
            value={date}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDate(e.target.value)}
          />
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium mb-1">Customer</label>
          <CustomerPicker
            value={customerId}
            onChange={setCustomerId}
            placeholder="Filter by customer..."
          />
        </div>
        <div className="flex items-end">
          <Button variant="secondary" onClick={() => refetch()} disabled={isLoading}>
            {isLoading ? 'Loading...' : 'Filter'}
          </Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        {isLoading ? (
          <CardContent className="p-8">
            <Skeleton rows={5} />
          </CardContent>
        ) : isError ? (
          <CardContent className="p-8 text-center text-danger-600">
            Failed to load late fee payments
            <Button variant="secondary" size="sm" className="ml-3" onClick={() => refetch()}>
              Retry
            </Button>
          </CardContent>
        ) : payments.length === 0 ? (
          <CardContent className="p-8 text-center text-text-secondary">
            No late fee payments found
          </CardContent>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-2">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Receipt #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Customer #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Agent</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {payments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-surface-1">
                      <td className="px-4 py-3 text-sm font-mono text-text-primary">{payment.receiptNumber}</td>
                      <td className="px-4 py-3 text-sm text-text-primary">
                        {payment.receiptDate ? new Date(payment.receiptDate).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-primary">{payment.customerName || '—'}</td>
                      <td className="px-4 py-3 text-sm font-mono text-text-secondary">{payment.customerNumber || '—'}</td>
                      <td className="px-4 py-3 text-sm text-text-secondary">{payment.agentNumber || '—'}</td>
                      <td className="px-4 py-3 text-right font-mono text-success-600">
                        <Money paise={payment.amountPaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          payment.status === 'BOUNCED' ? 'bg-danger-bg text-danger-text' :
                          payment.status === 'COMPLETED' ? 'bg-success-bg text-success-text' :
                          'bg-warning-bg text-warning-text'
                        }`}>
                          {payment.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-4 py-3 bg-surface-2 border-t border-border text-sm text-text-secondary">
              <span>Page {meta?.page ?? page} of {Math.max(1, Math.ceil((meta?.total ?? 0) / 20))}</span>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                <Button variant="secondary" size="sm" disabled={page * 20 >= (meta?.total ?? 0)} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}